import { ICampusItem } from '../models/campuses.model';
import {
  ICacUsageReportPayload,
  ICacUsageRow,
  ICacUsageMetricValues,
} from '../models/statistics.model';
import { CAMPUS_DIV_DIRECT, CAMPUS_DIV_FRANCHISE } from '../constans';

export const CAC_TAB_OVERALL = 'overall';
export const CAC_TAB_DIRECT_SUM = 'direct_sum';
export const CAC_TAB_FRANCHISE_SUM = 'franchise_sum';

export interface ICacExportRow {
  key: string;
  label: string;
  metrics: Partial<ICacUsageMetricValues>;
}

export interface ICacExportMatrix {
  divisionLabel: string;
  campusLabel: string;
  canbAll?: Partial<ICacUsageMetricValues>;
  divisionSum?: Partial<ICacUsageMetricValues>;
  campus?: Partial<ICacUsageMetricValues>;
}

export function rowHeaderLabelForCacTab(tabKey: string): string {
  return tabKey.startsWith('campus:') ? '담임' : '캠퍼스';
}

/** 화면/엑셀 공통 — 탭 키에 따른 활용 통계 표 행 */
export function buildCacReportRowsForTab(
  tabKey: string,
  usagePayload: ICacUsageReportPayload | undefined,
  accessibleCampuses: ICampusItem[],
): ICacExportRow[] {
  if (!usagePayload) return [];
  const toRow = (r: ICacUsageRow): ICacExportRow => ({
    key: r.rowKey,
    label: r.label,
    metrics: r.metrics,
  });

  if (tabKey === CAC_TAB_OVERALL) {
    return (usagePayload.overallRows ?? []).map(toRow);
  }
  if (tabKey === CAC_TAB_DIRECT_SUM || tabKey === CAC_TAB_FRANCHISE_SUM) {
    const sumKey =
      tabKey === CAC_TAB_DIRECT_SUM ? 'direct_sum' : 'franchise_sum';
    const div =
      tabKey === CAC_TAB_DIRECT_SUM ? CAMPUS_DIV_DIRECT : CAMPUS_DIV_FRANCHISE;
    const sumRow = usagePayload.aggregateRows?.find(
      (r) => r.rowKey === sumKey,
    );
    const campusCodesInDiv = new Set(
      accessibleCampuses
        .filter((c) => c.campusDiv === div)
        .map((c) => c.campusCode),
    );
    const campusRows = (usagePayload.overallRows ?? []).filter(
      (r) =>
        r.rowKey.startsWith('campus_') &&
        campusCodesInDiv.has(r.rowKey.slice('campus_'.length)),
    );
    const rows = sumRow ? [sumRow, ...campusRows] : campusRows;
    return rows.map(toRow);
  }
  if (tabKey.startsWith('campus:')) {
    const code = tabKey.slice('campus:'.length);
    const block = usagePayload.campusBlocks?.find(
      (b) => b.campusCode === code,
    );
    return (block?.teacherRows ?? []).map((r) => ({
      key: r.rowKey,
      label: r.rowKey.startsWith('teacher_')
        ? r.teacherName ?? r.label
        : r.label,
      metrics: r.metrics,
    }));
  }
  return [];
}

/** 캠퍼스 직원 + 캠퍼스 탭일 때만 매트릭스 데이터 */
export function buildCacMatrixForExportTab(
  tabKey: string,
  usagePayload: ICacUsageReportPayload | undefined,
  campusData: ICampusItem[] | undefined,
  hqYn: string,
): ICacExportMatrix | undefined {
  if (hqYn !== 'N' || !tabKey.startsWith('campus:') || !usagePayload) {
    return undefined;
  }
  const campusCode = tabKey.slice('campus:'.length);
  const campus = campusData?.find((c) => c.campusCode === campusCode);
  const divisionLabel =
    campus?.campusDiv === CAMPUS_DIV_FRANCHISE ? '가맹 소계' : '직영 소계';
  const campusLabel = `${campus?.campusKorName ?? '캠퍼스'} 합계`;
  const canbAll = usagePayload.overallRows?.find(
    (r) => r.rowKey === 'canb_total',
  )?.metrics;
  const aggKey =
    campus?.campusDiv === CAMPUS_DIV_FRANCHISE
      ? 'franchise_sum'
      : 'direct_sum';
  const divisionSum = usagePayload.aggregateRows?.find(
    (r) => r.rowKey === aggKey,
  )?.metrics;
  const block = usagePayload.campusBlocks?.find(
    (b) => b.campusCode === campusCode,
  );
  const campusMetrics = block?.teacherRows?.find(
    (r) => r.rowKey === 'campus_total',
  )?.metrics;
  return {
    divisionLabel,
    campusLabel,
    canbAll,
    divisionSum,
    campus: campusMetrics,
  };
}
